import { useEffect, useRef, useState } from 'react'
import { alpha, Box, Stack, Typography } from '@mui/material'

// Trail statuses are recalculated at :10 each hour; the public app treats them as available at :21 (after the
// refresh and cache regeneration finish), shows this same countdown, and refetches then. Keep these in sync with
// TrailDataFooter and the trail-system/list refetchInterval in flowfeed-ui.
export const TRAIL_DATA_UPDATE_MINUTE = 21

export const minutesSinceLastTrailDataUpdate = (now = new Date()) => {
  const thisMinute = now.getMinutes()
  return thisMinute >= TRAIL_DATA_UPDATE_MINUTE
    ? thisMinute - TRAIL_DATA_UPDATE_MINUTE
    : 60 - TRAIL_DATA_UPDATE_MINUTE + thisMinute
}

export const minutesUntilNextTrailDataUpdate = (now = new Date()) => {
  const thisMinute = now.getMinutes()
  return thisMinute >= TRAIL_DATA_UPDATE_MINUTE
    ? 60 - thisMinute + TRAIL_DATA_UPDATE_MINUTE
    : TRAIL_DATA_UPDATE_MINUTE - thisMinute
}

/**
 * @description Sticky footer bar showing how long ago trail statuses were updated and how long until the next update,
 * matching the bar at the bottom of the public app's trail system pages.
 */
const TrailDataUpdateBar = () => {
  const barRef = useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = useState(false)
  const [now, setNow] = useState(() => new Date())

  // tick once a minute so the counts stay current
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 60 * 1000)
    return () => window.clearInterval(interval)
  }, [])

  // add a backdrop once the bar is pinned to the bottom over the cards
  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    const observer = new IntersectionObserver(([entry]) => setIsStuck(entry.intersectionRatio < 1), { threshold: [1] })
    observer.observe(bar)
    return () => observer.disconnect()
  }, [])

  const count = (value: number, label: string, detail: string, muted?: boolean) => (
    <Stack alignItems="center">
      <Typography variant="body1" color={muted ? 'grey.500' : undefined} sx={{ fontSize: { zero: 12, sm: 16 } }}>
        {value} mins
      </Typography>
      <Typography variant="caption" color={muted ? 'grey.500' : undefined}>
        <Box component="span" sx={{ display: { zero: 'none', sm: 'inline' } }}>
          {detail}&nbsp;
        </Box>
        {label}
      </Typography>
    </Stack>
  )

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'sticky',
        // -1 lets the observer notice when the bar is pinned against the bottom edge
        bottom: -1,
        zIndex: 2,
        mt: 2,
        // run straight across the content area: cancel the page gutters (layout Container 16/24px + page 16px)
        // and pad back by the same amount so the labels line up with the cards
        mx: { zero: -4, sm: -5 },
        px: { zero: 4, sm: 5 },
        py: 1,
        ...(isStuck && {
          bgcolor: alpha('#000', 0.75),
          boxShadow: 4,
        }),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {count(minutesSinceLastTrailDataUpdate(now), 'last update', 'since', true)}
        {count(minutesUntilNextTrailDataUpdate(now), 'next update', 'until')}
      </Stack>
    </Box>
  )
}
export default TrailDataUpdateBar
